import { keepPreviousData } from '@tanstack/react-query';

import { PATH_SERIES_ID } from '@/features/time-series-chart/config/diagramIdentifiers';
import {
  TIME_SERIES_QUERY_CACHE_TIME_MS,
  TIME_SERIES_GARBAGE_COLLECTION_TIME_MS,
} from '@/features/time-series-chart/config/queryConfig';
import type { TimeWindowMicros } from '@/features/time-series-chart/types/TimeWindow.types';
import { DISABLED_TIME_WINDOW } from '@/features/time-series-chart/utils/timeWindow';
import {
  type getDataSeriesForTarget,
  getGetDataSeriesForTargetQueryKey,
  useGetDataSeriesForTarget,
} from '@/generated/client/diagram/diagram';
import type { DataSeries } from '@/generated/model';

import { getBindingIds } from './bindingIds';
import { getMeasureChannelIds } from './measureChannelIds';

type UseDataSeriesQueryParams = {
  targetId: number | undefined;
  timeWindow: TimeWindowMicros | undefined;
  selectedSeriesIds: string[];
};

type DataSeriesResponse = Awaited<ReturnType<typeof getDataSeriesForTarget>>;

function selectDataSeries(response: DataSeriesResponse): DataSeries[] {
  return response.data.series;
}

/**
 * Samples of the selected series. Toggling a series changes the query key; keeping the previous data
 * lets the chart stay mounted and only the changed series animate in, instead of a spinner replacing it.
 */
export function useDataSeriesQuery({ targetId, timeWindow, selectedSeriesIds }: UseDataSeriesQueryParams) {
  const isEnabled = targetId !== undefined && timeWindow !== undefined && selectedSeriesIds.length > 0;
  const requestTargetId = targetId ?? 0;
  const params = {
    measureChannelIds: getMeasureChannelIds(selectedSeriesIds),
    bindingIds: getBindingIds(selectedSeriesIds),
    includeSpeed: selectedSeriesIds.includes(PATH_SERIES_ID.SPEED),
    includeIgnition: selectedSeriesIds.includes(PATH_SERIES_ID.IGNITION),
    ...(timeWindow ?? DISABLED_TIME_WINDOW),
  };

  return useGetDataSeriesForTarget(requestTargetId, params, {
    axios: { paramsSerializer: { indexes: null } },
    query: {
      queryKey: [...getGetDataSeriesForTargetQueryKey(requestTargetId, params), selectedSeriesIds],
      enabled: isEnabled,
      retry: false,
      placeholderData: keepPreviousData,
      staleTime: TIME_SERIES_QUERY_CACHE_TIME_MS,
      gcTime: TIME_SERIES_GARBAGE_COLLECTION_TIME_MS,
      select: selectDataSeries,
    },
  });
}
